/**
 * Who may run a list now, and who may archive, unarchive or delete it (PREC-4537).
 *
 * The app's controls and the API must answer the same question: a control the app does not offer
 * must not be reachable by a direct call or an MCP tool. This module is that one answer. Its inputs
 * are the three fields every list view carries — `status`, `archived` and `processingStatus` —
 * whose values match `ListStatus` / `ListProcessingStatus` in the API and the web.
 *
 * The refusal codes are the API's error codes (`Errors`), so a caller turns a reason into the
 * exception or the user-facing sentence it already has one for.
 */

/** The statuses and run states the decisions compare against (values of both apps' enums). */
export const LIST_LIFECYCLE_STATE = {
	ACTIVE: "ACTIVE",
	IDLE: "IDLE",
	PROCESSING: "PROCESSING",
} as const;

/** The fields of a list the lifecycle decisions read. */
export interface ListLifecycleView {
	status: string;
	archived?: boolean | null;
	processingStatus?: string | null;
}

export type LifecycleDecision<R extends string> =
	| { allowed: true }
	| { allowed: false; reason: R };

/** Why Run now refuses a list. */
export type RunNowRefusalReason = "LIST_ARCHIVED" | "LIST_NOT_ACTIVE" | "LIST_NOT_IDLE";

/** Why archive, unarchive or delete refuses a list. */
export type ArchiveDeleteRefusalReason = "LIST_IS_PROCESSING";

/**
 * Run now is offered only on an active, unarchived list that is waiting for its next run (IDLE).
 * A run in flight would be started a second time and charged twice; a finished, failed, paused,
 * archived or out-of-credit list is not offered a run. An archived list answers with its own reason
 * even when it still reads ACTIVE, because that is the state the archive leaves it in.
 */
export function runNowEligibility(
	list: ListLifecycleView
): LifecycleDecision<RunNowRefusalReason> {
	if (list.archived) {
		return { allowed: false, reason: "LIST_ARCHIVED" };
	}

	if (list.status !== LIST_LIFECYCLE_STATE.ACTIVE) {
		return { allowed: false, reason: "LIST_NOT_ACTIVE" };
	}

	if (list.processingStatus !== LIST_LIFECYCLE_STATE.IDLE) {
		return { allowed: false, reason: "LIST_NOT_IDLE" };
	}

	return { allowed: true };
}

/**
 * Archive, unarchive and delete wait for a running list: its rows are being written and the run
 * owns the schedule. The list's own run verdicts (DONE, ERROR, …) do not block them.
 */
export function archiveDeleteEligibility(
	list: ListLifecycleView
): LifecycleDecision<ArchiveDeleteRefusalReason> {
	return list.processingStatus === LIST_LIFECYCLE_STATE.PROCESSING
		? { allowed: false, reason: "LIST_IS_PROCESSING" }
		: { allowed: true };
}
